/*
 * Boston Proper × Bloomreach — "Best Seller" / "Trending" badges.
 *
 * Isolated from any single section so both the PDP (bp-tag-pills block)
 * and product cards (bp-product-card.liquid, used across the carousel,
 * collection/PLP, most-loved, styled-for-you, etc.) can ask the same
 * question — "is this pid in widget X's top results?" — without each
 * caller re-fetching the widget itself.
 *
 * Both widgets are Bloomreach Pathways "global" widgets (site-wide
 * ranking, no item_ids/cat_ids needed):
 *   - Best Sellers: ojdx2149
 *   - Trending:     6j835y6l
 * rows=50 / fields=pid only — nothing here renders a card from the BR
 * response, it's purely a membership check, so a "for now" approximation
 * against the top 50 (not the full catalog) is what we get.
 *
 * Config (enabled + label) comes from window.shop.badges, set by
 * bp-shop-globals.liquid from Theme settings → Product cards → Badges.
 *
 * Public surface:
 *   window.BpBadges.applyToPdpPills(pillListEl, pid)
 *   window.BpBadges.applyToCards(rootEl?)   // (re)scan cards for badges
 */
(function () {
  'use strict';

  if (window.BpBadges) return; // idempotent, same convention as BpCardHydrate

  const HOST = 'https://pathways.dxpapi.com/api/v2/widgets/global/';
  const WIDGETS = {
    bestseller: 'ojdx2149',
    trending:   '6j835y6l'
  };

  const pidCache = {}; // widgetKey -> Promise<string[]>

  function getBrUid() {
    // Empty (not a fabricated uid=0:v=app:ts=0:hc=1 sentinel) when the BR
    // cookie is absent — BR flags that sentinel as invalid. The empty param
    // is stripped below so anonymous calls simply omit _br_uid_2.
    const m = document.cookie.match(/(?:^|;\s*)_br_uid_2=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function badgeConfig() {
    return (window.shop && window.shop.badges) || {};
  }

  // One fetch per widget per page load, however many callers ask.
  function fetchPids(widgetKey) {
    if (pidCache[widgetKey]) return pidCache[widgetKey];

    const br = (window.shop && window.shop.br) || {};
    const params = new URLSearchParams({
      account_id: br.accountId || '',
      domain_key: br.domainKey || 'bostonproper',
      rows:       '50',
      fields:     'pid',
      _br_uid_2:  getBrUid(),
      url:        window.location.href,
      ref_url:    document.referrer || window.location.href
    });
    if (!params.get('_br_uid_2')) params.delete('_br_uid_2');
    // auth_key sent only when configured (BR audit); never empty.
    if (br.authKey) params.set('auth_key', br.authKey);

    const fetchUrl = `${HOST}${WIDGETS[widgetKey]}?${params}`;
    pidCache[widgetKey] = fetch(fetchUrl, { credentials: 'omit' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const docs = (data && data.response && data.response.docs) || [];
        return docs.map((doc) => String(doc.pid));
      })
      .catch((err) => {
        console.warn(`[bp-badges] ${widgetKey} fetch failed`, err);
        return [];
      });

    return pidCache[widgetKey];
  }

  // Resolves only the widgets that are actually enabled in Theme settings.
  async function checkProduct(pid) {
    const cfg = badgeConfig();
    const flags = {};
    const checks = [];

    if (cfg.bestseller && cfg.bestseller.enabled) {
      checks.push(fetchPids('bestseller').then((pids) => {
        flags.bestseller = pids.includes(String(pid));
      }));
    }
    if (cfg.trending && cfg.trending.enabled) {
      checks.push(fetchPids('trending').then((pids) => {
        flags.trending = pids.includes(String(pid));
      }));
    }

    await Promise.all(checks);
    return flags;
  }

  // --- PDP: [data-bp-pills] under the title (blocks/bp-tag-pills.liquid) ---
  // BR badges take priority over the Liquid-rendered tag pills: when
  // either widget matches, the tag pills are cleared so the row never
  // shows both a BR badge and a tag pill at once.
  async function applyToPdpPills(pillListEl, pid) {
    if (!pillListEl) return;
    const cfg = badgeConfig();
    const flags = await checkProduct(pid);
    if (!flags.bestseller && !flags.trending) return;

    pillListEl.innerHTML = '';
    ['bestseller', 'trending'].forEach((key) => {
      if (!flags[key]) return;
      const li = document.createElement('li');
      li.className = 'bp-pills__item';
      li.textContent = cfg[key].label;
      li.setAttribute(`data-bp-pill-${key}`, '');
      pillListEl.appendChild(li);
    });
    pillListEl.classList.remove('bp-pills--empty');
  }

  // --- Product cards: [data-bp-card-badges] (snippets/bp-product-card.liquid) ---
  // Same priority rule: a matching BR badge replaces the metafield-driven
  // .bp-card__badge rather than stacking alongside it.
  async function applyToCard(article) {
    if (!article || article.dataset.bpBadgesApplied === '1') return;
    article.dataset.bpBadgesApplied = '1';

    const pid = article.dataset.productId;
    const badgesEl = article.querySelector('[data-bp-card-badges]');
    if (!pid || !badgesEl) return;

    const cfg = badgeConfig();
    const flags = await checkProduct(pid);
    if (!flags.bestseller && !flags.trending) return;

    // Only ONE badge per card. Priority order: Popular (bestseller) wins
    // over Trending — a product flagged both shows Popular only.
    const key = ['bestseller', 'trending'].find((k) => flags[k]);
    if (!key) return;

    badgesEl.innerHTML = '';
    const span = document.createElement('span');
    span.className = 'bp-card__badge';
    span.textContent = cfg[key].label;
    span.setAttribute(`data-bp-badge-${key}`, '');
    badgesEl.appendChild(span);
  }

  function applyToCards(root) {
    const scope = root || document;
    scope.querySelectorAll('.bp-card[data-product-id]:not([data-bp-badges-applied="1"])')
      .forEach((article) => applyToCard(article));
  }

  window.BpBadges = { applyToPdpPills, applyToCards };

  // The initial card scan triggers the two pathways.dxpapi.com widget
  // fetches (rows=50 membership check). Defer it to idle so it doesn't
  // compete with the LCP at page load — requestIdleCallback, or a 1.5s
  // timeout fallback. The cached-promise behavior (one fetch per widget)
  // and the post-BR-render handOffTo reapply (bp-bloomreach.js calls
  // applyToCards again on freshly rendered cards) are unaffected: they
  // hit the same pidCache and re-run applyToCards on demand.
  const startBadgeScan = () => applyToCards(document);
  const scheduleIdle = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(startBadgeScan, { timeout: 3000 });
    } else {
      setTimeout(startBadgeScan, 1500);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleIdle);
  } else {
    scheduleIdle();
  }
})();
